import { format } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Locales } from 'shared/assets/localization';
import { TimeslotContextMenu } from 'shared/components/connected/timeslot-context-menu/timeslot-context-menu';
import { TaskMetaInteraction } from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { Task } from 'shared/types/task';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { formatDurationLabel } from 'shared/utils/format-duration-label';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './task-meta-interaction-due-date.style';

export type TaskMetaInteractionTimeslotProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  task: Task;
  timeFormat: TimeFormatOptions;
  representedDay: Timestamp;
  value?: Timeslot | null;
  hasDueDate?: boolean;
};

export const TaskMetaInteractionTimeslot: React.FC<
  TaskMetaInteractionTimeslotProps
> = ({
  task,
  timeFormat,
  representedDay,
  value,
  hasDueDate,

  ...rest
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const label = useMemo(() => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, value?.startTime ?? 0);
    const endDate = new Date();
    endDate.setHours(0, 0, 0, (value?.startTime ?? 0) + (value?.duration ?? 0));

    const formattedStart = isNumber(value?.startTime)
      ? format(startDate, timeFormat)
      : undefined;
    const formattedEnd =
      value?.duration && isNumber(value.startTime)
        ? format(endDate, timeFormat)
        : undefined;

    if (formattedStart && formattedEnd) {
      // Check for AM/PM match
      const startMeridiem = formattedStart.slice(-2);
      const endMeridiem = formattedEnd.slice(-2);

      if (
        startMeridiem === endMeridiem &&
        ['AM', 'PM'].includes(startMeridiem)
      ) {
        return `${formattedStart.replace(` ${startMeridiem}`, '')}-${formattedEnd}`;
      }

      return `${formattedStart} - ${formattedEnd}`;
    }

    return [
      formattedStart,
      value?.duration && !isNumber(value.startTime)
        ? formatDurationLabel(value.duration, locale === Locales.NlNL)
        : undefined,
    ]
      .filter(Boolean)
      .join(' - ');
  }, [locale, timeFormat, value?.duration, value?.startTime]);

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        onClick={openMenu}
        label={label}
        selected={menuOpen}
        hasTooltip
        tooltipLabel={t('task.schedule.skip.tooltip.label')}
        minimalStartSpacing={hasDueDate}
        {...rest}
      />

      {menuOpen && (
        <TimeslotContextMenu
          referenceElement={containerRef}
          task={task}
          representedDay={representedDay}
          onClose={closeMenu}
          position="bottom-start"
        />
      )}
    </Styled.Container>
  );
};
